import { PlayerIcon } from '@movingimage-evp/mi-ui-component-library';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { crosshairPlugin } from './utils/crosshair-plugin';
import { externalTooltip } from './utils/external-tooltip';
import { formatTime } from './utils/format-time';
import { getCssVar } from './utils/get-css-var';
import { type EventTimelineDataPoint } from '../../../../../../generated/graphql-manager';

import styles from './active-plays-chart.module.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface ActivePlaysChartProps {
  dataPoints: EventTimelineDataPoint[];
}

export function ActivePlaysChart({ dataPoints }: ActivePlaysChartProps) {
  const { t } = useTranslation();
  const [isChartHovered, setIsChartHovered] = useState(false);

  const primaryMain = getCssVar('--mi-primary-main');
  const success60 = getCssVar('--mi-success-60');
  const axisColor = getCssVar('--mi-static-black');
  const tickColor = getCssVar('--mi-label-subtle');
  const fontFamily = getCssVar('--font-family') || 'Montserrat, sans-serif';

  if (dataPoints.length <= 1) return null;

  return (
    <section className={styles.container} data-testid="active-plays-chart">
      <div className={styles.header}>
        <PlayerIcon />
        <span className={styles.title}>{t('manager.eventSummary.activePlaysChart.title')}</span>
      </div>

      <div className={styles.chartWrapper}>
        <Line
          onMouseLeave={() => setIsChartHovered(false)}
          plugins={[crosshairPlugin]}
          data={{
            labels: dataPoints.map(({ timestamp }) => formatTime(timestamp)),
            datasets: [
              {
                label: t('manager.eventSummary.activePlaysChart.activePlays'),
                data: dataPoints.map(({ activePlays }) => activePlays),
                borderColor: isChartHovered ? success60 : 'transparent',
                borderWidth: 1,
                backgroundColor: `color-mix(in srgb, ${primaryMain} 18%, transparent)`,
                fill: true,
                tension: 0,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHoverBackgroundColor: primaryMain,
                pointHoverBorderColor: primaryMain,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            onHover: (_, elements) => {
              const hovered = elements.length > 0;
              if (hovered !== isChartHovered) setIsChartHovered(hovered);
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: false,
                external: (context) => {
                  const dataIndex = context.tooltip.dataPoints?.[0]?.dataIndex;

                  if (dataIndex === undefined) return;

                  const point = dataPoints[dataIndex];

                  if (!point) return;

                  externalTooltip(context, [
                    t('manager.eventSummary.activePlaysChart.activePlaysValue', { value: point.activePlays }),
                    t('manager.eventSummary.activePlaysChart.time', { time: formatTime(point.timestamp) }),
                    t('manager.eventSummary.activePlaysChart.streamMinute', { minute: point.minuteFromStart }),
                  ]);
                },
              },
            },
            scales: {
              x: {
                grid: { drawOnChartArea: false, drawTicks: true, tickColor: axisColor },
                border: { color: axisColor },
                ticks: {
                  color: tickColor,
                  font: { family: fontFamily, size: 12 },
                  maxTicksLimit: 24,
                  autoSkip: true,
                  major: { enabled: true },
                },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: tickColor,
                  font: { family: fontFamily, size: 12 },
                  maxTicksLimit: 5,
                  precision: 0,
                  callback: (value) => ` ${value}`,
                },
                grid: {
                  drawOnChartArea: false,
                  drawTicks: true,
                  tickColor: axisColor,
                },
                border: { color: axisColor },
              },
            },
          }}
        />
      </div>
    </section>
  );
}
